// Tools shared by every Smart CMS section: the review queue, owners and review dates, review requests,
// publishing saved changes, what changed between versions, the list filters and the media library

import { tool } from "ai"
import { z } from "zod"
import type { DueItem, PendingItem } from "@/pages/dev/smart-cms/shared/components/PendingChanges"
import { filterTarget, readViews, writeViews } from "@/pages/dev/smart-cms/shared/components/RecordFilters"
import { scanMedia, teamUploads } from "@/pages/dev/smart-cms/shared/components/MediaLibrary"
import { fieldChanges } from "@/pages/dev/smart-cms/shared/historyDiff"
import { ago, DEFAULT_REVIEW_MONTHS, freshnessOf, inTime } from "@/pages/dev/smart-cms/shared/useRecordMeta"
import type { MetaMap, Person, RecordMeta } from "@/pages/dev/smart-cms/shared/persistence/recordMeta"

export interface WorkflowVersion {
  entryId: string
  at: number
  author: string
  summary: string
  snapshot?: Record<string, any>
}

export interface WorkflowToolContext {
  // Which list the filters belong to; Site Configuration has none
  filterSection: "articles" | "services" | null
  // The record's key ("bucket:id") in the open section, or null when it isn't there
  keyFor: (id: string) => string | null
  titleOf: (key: string) => string
  working: (key: string) => Record<string, any> | undefined
  live: (key: string) => Record<string, any> | undefined
  pending: () => PendingItem[]
  isPending: (key: string) => boolean
  due: () => DueItem[]
  meta: () => MetaMap
  metaAvailable: () => boolean
  people: () => Person[]
  me: () => { id?: string; name: string }
  canPublish: () => boolean
  updateMeta: (key: string, patch: Partial<RecordMeta>) => void
  clearReview: (key: string) => void
  publish: (key: string) => void
  discard: (key: string) => void
  versions: (id: string) => WorkflowVersion[]
  open: (key: string) => void
  mediaSources: () => { label: string; records: Record<string, any> }[]
}

const fail = (message: string) => ({ error: message })
const clip = (s: string, n = 600) => (s.length > n ? `${s.slice(0, n)}…` : s)
const changesOf = (before: any, after: any) =>
  fieldChanges(before, after).map(c => ({ field: c.label, before: clip(c.before), after: clip(c.after) }))

export const WORKFLOW_TOOL_LABELS: Record<string, string> = {
  workflow_review_queue: "Review Queue",
  workflow_record_details: "Record Details",
  workflow_show_changes: "Show Changes",
  workflow_get_filters: "Get Filters",
  workflow_set_filters: "Set Filters",
  workflow_save_view: "Save View",
  media_library: "Media Library",
  workflow_request_review: "Request Review",
  workflow_cancel_review: "Cancel Review",
  workflow_set_owner: "Set Owner and Schedule",
  workflow_mark_verified: "Mark Verified",
  workflow_publish_changes: "Publish Changes",
  workflow_discard_changes: "Discard Changes",
}

export function makeWorkflowTools(ctx: WorkflowToolContext) {
  const keyOrFail = (id: string) => ctx.keyFor(id)
  const findPerson = (who: string) => {
    const q = who.trim().toLowerCase()
    const people = ctx.people()
    if (q === "me" || q === "myself") return people.find(p => p.id === ctx.me().id)
    return people.find(p => p.id === who) ?? people.find(p => p.name.toLowerCase() === q) ?? people.find(p => p.name.toLowerCase().includes(q))
  }
  const detailsOf = (key: string) => {
    const m = ctx.meta()[key]
    const f = freshnessOf(ctx.working(key), m)
    const pending = ctx.pending().find(p => p.key === key)
    return {
      title: ctx.titleOf(key),
      owner: m?.owner?.name ?? null,
      reviewEveryMonths: m?.reviewEveryMonths ?? DEFAULT_REVIEW_MONTHS,
      lastChecked: f ? ago(f.checkedAt) : null,
      reviewDue: f ? (f.state === "overdue" ? "overdue" : inTime(f.dueAt)) : "never",
      verifiedBy: m?.verifiedBy ?? null,
      changesNotLive: pending ? pending.summary : null,
      reviewRequest: m?.review
        ? { assignee: m.review.assignee?.name ?? "any manager", requestedBy: m.review.requestedBy ?? null, note: m.review.note ?? null }
        : null,
    }
  }
  const needsMeta = () => (ctx.metaAvailable() ? null : "Record details aren't available for this team.")

  return {
    workflow_review_queue: tool({
      description:
        "The open section's Review queue: saved changes that aren't live yet (with who's asked to review them), " +
        "review requests assigned to the current user, and live content due or overdue for an accuracy check.",
      parameters: z.object({}),
      execute: async () => {
        const pending = ctx.pending()
        return {
          unpublished: pending.map(p => ({ id: p.key.slice(p.key.indexOf(":") + 1), kind: p.kind, title: p.title, changes: p.summary, reviewer: p.reviewer ?? null })),
          assignedToMe: pending.filter(p => p.assignedToMe).map(p => p.title),
          dueForReview: ctx.due().map(d => ({ id: d.key.slice(d.key.indexOf(":") + 1), kind: d.kind, title: d.title, overdue: d.overdue, detail: d.detail })),
          canPublish: ctx.canPublish(),
        }
      },
    }),

    workflow_record_details: tool({
      description: "A record's owner, review interval, when it was last checked and when it's due, any changes not live yet, and any review request.",
      parameters: z.object({ id: z.string().describe("The record's id") }),
      execute: async ({ id }) => {
        const key = keyOrFail(id)
        return key ? detailsOf(key) : fail(`No record ${id} in this section.`)
      },
    }),

    workflow_show_changes: tool({
      description:
        "What changed in a record, field by field. With entryId null: the saved changes that aren't live yet, " +
        "or, if there are none, the most recent version. With an entryId from the versions tool: that version against the one before it.",
      parameters: z.object({
        id: z.string(),
        entryId: z.string().nullable().describe("A version's entryId, or null"),
      }),
      execute: async ({ id, entryId }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        if (!entryId && ctx.isPending(key)) {
          return { comparing: "live version → changes not live yet", changes: changesOf(ctx.live(key), ctx.working(key)) }
        }
        const versions = ctx.versions(id).filter(v => v.snapshot)
        const i = entryId ? versions.findIndex(v => v.entryId === entryId) : 0
        if (i < 0 || !versions[i]) return fail(entryId ? `No version ${entryId} with a snapshot.` : "This record has no recorded versions.")
        const v = versions[i]
        const older = versions[i + 1]
        return {
          comparing: `${older ? `version of ${new Date(older.at).toLocaleString()}` : "nothing (first version)"} → ${new Date(v.at).toLocaleString()} by ${v.author}`,
          summary: v.summary,
          changes: changesOf(older?.snapshot, v.snapshot),
        }
      },
    }),

    workflow_get_filters: tool({
      description: "The open list's filters: every filter with its allowed values, what's set now, and the user's saved views.",
      parameters: z.object({}),
      execute: async () => {
        const section = ctx.filterSection
        if (!section) return fail("Site Configuration has no list filters.")
        const t = filterTarget(section)
        if (!t) return fail(section === "services" ? "The Services list isn't on screen. Ask the user to switch the Service Map to the Services view." : "The article list isn't on screen.")
        return {
          filters: t.defs.map(d => ({ key: d.key, label: d.label, values: d.options.map(o => ({ value: o.value, label: o.label })) })),
          current: t.value,
          savedViews: readViews(section).map(v => v.name),
        }
      },
    }),

    workflow_set_filters: tool({
      description:
        "Set the open list's filters, replacing the current ones, so the user sees only matching records. " +
        "Use keys and values from workflow_get_filters. Pass an empty list to clear, or viewName to apply a saved view.",
      parameters: z.object({
        filters: z.array(z.object({ key: z.string(), value: z.string() })).describe("Filters to set; empty clears them"),
        viewName: z.string().nullable().describe("A saved view to apply instead, or null"),
      }),
      execute: async ({ filters, viewName }) => {
        const section = ctx.filterSection
        if (!section) return fail("Site Configuration has no list filters.")
        const t = filterTarget(section)
        if (!t) return fail("The list isn't on screen, so its filters can't be set.")
        if (viewName) {
          const view = readViews(section).find(v => v.name.toLowerCase() === viewName.toLowerCase())
          if (!view) return fail(`No saved view "${viewName}".`)
          t.onChange(view.filters)
          return { applied: view.name, filters: view.filters }
        }
        const next: Record<string, string> = {}
        for (const f of filters) {
          const def = t.defs.find(d => d.key === f.key)
          if (!def) return fail(`Unknown filter "${f.key}". Filters: ${t.defs.map(d => d.key).join(", ")}`)
          const opt = def.options.find(o => o.value === f.value) ?? def.options.find(o => o.label.toLowerCase() === f.value.toLowerCase())
          if (!opt) return fail(`"${f.value}" isn't a value of ${def.label}. Values: ${def.options.map(o => o.value).join(", ")}`)
          next[def.key] = opt.value
        }
        t.onChange(next)
        return { filters: next }
      },
    }),

    workflow_save_view: tool({
      description: "Save the list's current filters as a named view in the user's browser, so they can apply it again from Views.",
      parameters: z.object({ name: z.string() }),
      execute: async ({ name }) => {
        const section = ctx.filterSection
        const t = section && filterTarget(section)
        if (!section || !t) return fail("There's no list with filters on screen.")
        if (!Object.keys(t.value).length) return fail("No filters are set, so there's nothing to save.")
        const n = name.trim()
        writeViews(section, [...readViews(section).filter(v => v.name !== n), { name: n, filters: t.value }])
        return { saved: n, filters: t.value }
      },
    }),

    media_library: tool({
      description:
        "Images this team already uses (with where) or has uploaded. Use a URL from here to reuse an image in an image field or an article body instead of asking for an upload.",
      parameters: z.object({ query: z.string().nullable().describe("Words from where it's used or its file name, or null for all") }),
      execute: async ({ query }) => {
        const used = scanMedia(ctx.mediaSources())
        const known = new Set(used.map(i => i.url))
        const uploads = (await teamUploads()).filter(u => !known.has(u)).map(url => ({ url, usedIn: [] as string[] }))
        const q = query?.trim().toLowerCase()
        const all = [...used, ...uploads].filter(i => !q || i.url.toLowerCase().includes(q) || i.usedIn.some(u => u.toLowerCase().includes(q)))
        return { count: all.length, images: all.slice(0, 50).map(i => ({ url: i.url, usedIn: i.usedIn.length ? i.usedIn : ["not used yet"] })) }
      },
    }),

    workflow_request_review: tool({
      description:
        "Ask someone to review a record's saved changes that aren't live yet. reviewer is a team member's name, or null for any manager.",
      parameters: z.object({
        id: z.string(),
        reviewer: z.string().nullable(),
        note: z.string().nullable(),
      }),
      execute: async ({ id, reviewer, note }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        const missing = needsMeta()
        if (missing) return fail(missing)
        if (!ctx.isPending(key)) return fail("This record has no saved changes waiting to go live, so there's nothing to review.")
        const assignee = reviewer ? findPerson(reviewer) : undefined
        if (reviewer && !assignee) return fail(`No team member matches "${reviewer}". Team: ${ctx.people().map(p => p.name).join(", ")}`)
        ctx.updateMeta(key, { review: { assignee, requestedBy: ctx.me().name, requestedAt: Date.now(), note: note || undefined } })
        ctx.open(key)
        return { requested: ctx.titleOf(key), reviewer: assignee?.name ?? "any manager" }
      },
    }),

    workflow_cancel_review: tool({
      description: "Cancel the review request on a record.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        if (!ctx.meta()[key]?.review) return fail("This record has no review request.")
        ctx.clearReview(key)
        return { cancelled: ctx.titleOf(key) }
      },
    }),

    workflow_set_owner: tool({
      description:
        "Set who owns a record (responsible for keeping it accurate) and how often it should be checked. " +
        "owner: a team member's name, \"me\" for the current user, \"none\" to remove, or null to leave as is. reviewEveryMonths: 3, 6, 12, 0 for never, or null to leave as is.",
      parameters: z.object({
        id: z.string(),
        owner: z.string().nullable(),
        reviewEveryMonths: z.number().int().nullable(),
      }),
      execute: async ({ id, owner, reviewEveryMonths }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        const missing = needsMeta()
        if (missing) return fail(missing)
        const patch: Partial<RecordMeta> = {}
        if (owner) {
          if (owner.toLowerCase() === "none") patch.owner = undefined
          else {
            const p = findPerson(owner)
            if (!p) return fail(`No team member matches "${owner}". Team: ${ctx.people().map(x => x.name).join(", ")}`)
            patch.owner = p
          }
        }
        if (reviewEveryMonths != null) {
          if (![0, 3, 6, 12].includes(reviewEveryMonths)) return fail("reviewEveryMonths must be 3, 6, 12 or 0 (never).")
          patch.reviewEveryMonths = reviewEveryMonths === DEFAULT_REVIEW_MONTHS ? undefined : reviewEveryMonths
        }
        if (!Object.keys(patch).length) return fail("Nothing to change.")
        ctx.updateMeta(key, patch)
        return detailsOf(key)
      },
    }),

    workflow_mark_verified: tool({
      description: "Record that someone confirmed a live record is still accurate. This restarts its review countdown. Only after the user confirms it's correct.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        const missing = needsMeta()
        if (missing) return fail(missing)
        ctx.updateMeta(key, { verifiedAt: Date.now(), verifiedBy: ctx.me().name })
        return detailsOf(key)
      },
    }),

    workflow_publish_changes: tool({
      description: "Managers only: put one record's saved changes live on the public site. One record per call.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        if (!ctx.canPublish()) return fail("Only a manager can publish. Offer to request a review instead.")
        if (!ctx.isPending(key)) return fail("This record has no changes waiting to go live.")
        ctx.publish(key)
        ctx.clearReview(key)
        return { published: ctx.titleOf(key) }
      },
    }),

    workflow_discard_changes: tool({
      description: "Throw away one record's saved changes that aren't live yet; it goes back to its live version. One record per call.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const key = keyOrFail(id)
        if (!key) return fail(`No record ${id} in this section.`)
        if (!ctx.isPending(key)) return fail("This record has no changes waiting to go live.")
        ctx.discard(key)
        ctx.clearReview(key)
        return { discarded: ctx.titleOf(key) }
      },
    }),
  }
}
